import React, { useMemo } from 'react';
import GPTWrap, { IProps as IGPTWrapProps } from '@home/pages/studio/opinions/components/GPTWrap';
import { resolvePrHubUnifiedEditionId } from '@home/pages/studio/pr/_utils';
import type { TPrFilterVal } from '@home/pages/studio/pr/components/PrStudioMonthFilter';

/**
 * PR overview AI Summary 适配组件：
 * 桥接 `PrStudioMonthFilter` 注入的 `filterVal`（start_date/end_date/time_granularity）与 `data`，
 * 派生 `GPTWrap`/`GPTReport` 所需的 `filter`（含 start_time/end_time/date_type）和 `gameInfo.edition_unified_id`。
 */
interface IPrAiSummaryProps extends Omit<IGPTWrapProps, 'index' | 'filter'> {
  data?: Record<string, unknown>;
  filterVal?: TPrFilterVal;
  filterLoading?: boolean;
  index?: number;
}

type TPrAiSummaryGameInfo = {
  edition_unified_id: string;
  entity_type: 'pc';
  id_type: 'edition_id';
};

export default function PrAiSummary(props: IPrAiSummaryProps) {
  const { data, filterVal, filterLoading, index = 0, className, ...rest } = props;

  /** 当前 PR Hub 选中游戏的 unified_edition_id（resolvePrHubUnifiedEditionId 已处理 game_code/unified_edition_id 两种形态） */
  const gameInfo = useMemo<TPrAiSummaryGameInfo>(() => {
    const unifiedId = resolvePrHubUnifiedEditionId(data || {}) || '';
    return {
      edition_unified_id: unifiedId,
      entity_type: 'pc',
      id_type: 'edition_id',
    };
  }, [data]);

  /**
   * GPTWrap 期望 start_time/end_time（datetime）+ date_type；GPTWrap 内会再 split(' ')[0] 取日期段。
   * 同时必须携带 gameInfo（edition_unified_id/entity_type/id_type），否则 useDefaultSummary 中
   * `filter?.edition_unified_id` 判断不通过，请求不会发起，组件会一直停留在 loading 状态。
   * 与 OpinionsFilter 中 `filter = { ...gameInfo, ...timeValue }` 的构造方式保持一致。
   */
  const filter = useMemo(() => {
    if (!filterVal) {
      return { ...gameInfo };
    }
    return {
      ...gameInfo,
      ...filterVal,
      date_type: filterVal?.time_granularity,
      start_time: `${filterVal.start_date} 00:00:00`,
      end_time: `${filterVal.end_date} 23:59:59`,
    };
  }, [filterVal, gameInfo]);

  if (filterLoading) {
    return null;
  }

  /**
   * GPTWrap.IProps 未声明 data/gameInfo，但内部通过 ...resetProps 透传给 CommonTitle / GPTReport，
   * 与 OpinionsFilter cloneElement 注入路径一致。这里以 Partial<IGPTWrapProps> 局部断言显式表达
   * "桥接透传"语义，而不污染 GPTWrap 的对外契约。
   */
  const passthrough = { data, gameInfo } as Partial<IGPTWrapProps>;

  return (
    <GPTWrap
      {...rest}
      {...passthrough}
      index={index}
      filter={filter}
      filterCond={{}}
      className={className}
    />
  );
}
